/**
 * ProtectedRoute — Phase B.5
 *
 * Wraps app routes:
 *  - Shows loading while we figure out who the user is
 *  - Redirects to /login if not signed in
 *  - Redirects to the role's landing page if signed in but lacking access
 *    to the requested route
 */
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "../lib/AuthProvider";

export default function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading, canAccess, landingPath } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950 text-zinc-100">
        <div className="flex items-center gap-3 text-zinc-300">
          <Loader2 className="w-5 h-5 animate-spin text-emerald-400" />
          Loading…
        </div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  // If a route declares a specific allowed-roles list, enforce it.
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={landingPath} replace />;
  }
  // Otherwise fall back to the global role→route matrix from AuthProvider.
  if (!allowedRoles && !canAccess(location.pathname)) {
    return <Navigate to={landingPath} replace />;
  }
  return children;
}
